import {
  BadgeTitle,
  BadgeValue,
  CardBody,
  InfoBadge,
  RightContent,
} from '../styles';
import { useMemo, useState } from 'react';
import Skeleton from '@mui/material/Skeleton';
import Fallback from '@/components/Fallback';
import { useTheme } from '@mui/material/styles';
import { EthernetPort, Pencil, WifiPen } from 'lucide-react';
import ModalEditor from '@/components/ModalEditor';
import { enqueueSnackbar } from 'notistack';
import api from '@/services/api';
import Typography from '@mui/material/Typography';
import {
  ChartsGrid,
  ChartColumn,
  EditButton,
  LanGrid,
  ConnectionDetailsStack,
  UptimeInfoBadge,
  UptimeBadgeTitle,
  WanStatsTitle,
} from './styles';
import { formatDurationShort } from '@/utils/timeFormatter';
import { formatBytes } from '@/utils/formatters';
import { DescriptionComponent, MainHeader } from '../Wireless/styles';
import DescriptionPopper from '@/components/DescriptionPopper';
import SelectAp from '../Wireless/SelectAp';
import Connection from './Connections';
import PieArcChart from '@components/PieArcChart';
import type { PieValueType } from '@mui/x-charts';
import {
  BridgeFields,
  DhcpFields,
  genericWanFields,
  lanFields,
  PPPOEFields,
  StaticFields,
  wanFields,
} from '@/utils/constants';
import Card from '@/components/Card';
import { useTranslation } from 'react-i18next';
import { useEquipmentContext } from '@/context/EquipmentContext';
import {
  getLanSchema,
  getWanSchema,
  getBridgeSchema,
} from '@/validations/networkSchema';
import StatsData from '@/components/ModalEditor/StatsData';

/**
 * General information card component displaying equipment details
 * Shows WAN, LAN, and LAN port configurations with toggleable views
 */
function General({ modeView }: { modeView: string }) {
  const { t } = useTranslation();
  const [currentModal, setCurrentModal] = useState<
    'selectWan' | 'selectLan' | 'editWan' | 'editLan' | null
  >(null);
  const [currentWan, setCurrentWan] = useState(0);
  const [currentLan, setCurrentLan] = useState(0);
  const [selectedPort, setSelectedPort] = useState<
    LanPortType | PonPortType | null
  >(null);
  const { equipment, refetch, loading } = useEquipmentContext();

  const theme = useTheme();

  const wanExternalOptions = useMemo(() => {
    const wan = equipment?.wans?.[currentWan];
    if (!wan) return {};

    // Combine all possible field names from different WAN types
    const allWanFields = [
      ...DhcpFields,
      ...PPPOEFields,
      ...StaticFields,
      ...BridgeFields,
      ...genericWanFields,
    ];

    // Create externalOptions by extracting options from each field
    const options: Record<string, { options: string[] }> = {};
    allWanFields.forEach((field) => {
      const fieldData = wan[field.field as keyof NetworkInterfaceType];
      if (fieldData?.options && Array.isArray(fieldData.options)) {
        options[field.field] = { options: fieldData.options };
      }
    });

    return options;
  }, [equipment, currentWan]);

  const lanExternalOptions = useMemo(() => {
    const lan = equipment?.lans?.[currentLan];
    if (!lan) return {};

    // Create externalOptions by extracting options from each field
    const options: Record<string, { options: string[] }> = {};
    lanFields.forEach((field) => {
      const fieldData = lan[field.field as keyof NetworkInterfaceType];
      if (fieldData?.options && Array.isArray(fieldData.options)) {
        options[field.field] = { options: fieldData.options };
      }
    });

    return options;
  }, [equipment, currentLan]);

  const lanSettingSchema = useMemo(() => getLanSchema(t), [t]);

  const wanSettingSchema = useMemo(
    () =>
      equipment?.wans?.[currentWan]?.['connectionType']?.value === 'BRIDGE'
        ? getBridgeSchema(t, wanExternalOptions)
        : getWanSchema(t, wanExternalOptions),
    [t, equipment, currentWan, wanExternalOptions]
  );

  const wanGroupedOptions = useMemo(() => {
    const priority: Record<string, number> = { PPPOE: 1, DHCP: 2, STATIC: 3 };
    const groups: Record<string, { index: number; name: string }[]> = {};

    (equipment?.wans ?? []).forEach((current, index) => {
      const type = String(
        current?.connectionType?.value || 'Outros'
      ).toUpperCase();
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push({
        index,
        name: String(current?.alias?.value ?? `WAN ${index + 1}`),
      });
    });

    return Object.entries(groups)
      .sort(
        ([typeA], [typeB]) => (priority[typeA] || 99) - (priority[typeB] || 99)
      )
      .map(([group, items]) => ({ group, items }));
  }, [equipment?.wans]);

  // Dynamic field list based on WAN connection type
  const currentWanFieldList = useMemo(() => {
    const connectionType =
      equipment?.wans?.[currentWan]?.['connectionType']?.value;
    if (connectionType === 'DHCP') return DhcpFields;
    if (connectionType === 'PPPOE') return PPPOEFields;
    if (connectionType === 'STATIC') return StaticFields;
    if (connectionType === 'BRIDGE') return BridgeFields;
    return [];
  }, [equipment, currentWan]);

  if (!equipment) {
    return (
      <Fallback
        text={t('equipment.resume.loadingEquipment')}
        variant="compact"
      />
    );
  }

  const memoryTotal = Number(equipment.general?.memoryTotal?.value ?? 0);
  const memoryFree = Number(equipment.general?.memoryFree?.value ?? 0);
  const memoryUsed = memoryTotal - memoryFree;
  const memoryUsagePercent =
    memoryTotal > 0 ? Math.round((memoryUsed / memoryTotal) * 100) : 0;
  const memory = [
    {
      value: Number(memoryUsed),
      color: 'url(#pieChartGradient)',
      label: t('equipment.used'),
    },
    {
      value: Number(memoryFree),
      color: theme.palette.grey['05'],
      label: t('equipment.free'),
    },
  ];

  const memoryTooltipFormatter = (item: PieValueType) => {
    const memTotal = Number(equipment?.general?.memoryTotal?.value || 0);
    const percentage =
      memTotal > 0 ? Math.round((item.value / memTotal) * 100) : 0;
    // base 1000 shows MB/GB instead of MiB/GiB
    const formattedBytes = formatBytes(item.value * 1024, { base: 1000 });
    return `${formattedBytes}\n${percentage}% do total`;
  };

  const cpuRawValue = String(equipment.general?.cpuUsage?.value || '0');
  const cpuValue = Number(
    cpuRawValue.split(';')[0].replace('%', '').replace(',', '.') || 0
  );

  const cpuUsage = [
    {
      value: cpuValue,
      color: 'url(#pieChartGradient)',
      label: t('equipment.used'),
    },
    {
      value: 100 - cpuValue,
      color: theme.palette.grey['05'],
      label: t('equipment.free'),
    },
  ];

  const cpuTooltipFormatter = (item: PieValueType) => {
    return `${item.value}%`;
  };

  /** Update on router and database WAN changed fields */
  const handleEdit = async (
    interfaceType: 'wans' | 'lans',
    index: number,
    fieldsToCheck: { field: keyof NetworkInterfaceType; type: string }[],
    values: Record<string, string | number>
  ) => {
    if (!equipment || !equipment.model?.id) return;

    const interfaceData = equipment?.[interfaceType]?.[index];

    const changedFields = fieldsToCheck
      .map((field) => {
        const current =
          interfaceData?.[field.field as keyof NetworkInterfaceType]?.value;
        const id =
          interfaceData?.[field.field as keyof NetworkInterfaceType]?.id;
        const newValue = values[field.field] ?? '';

        if (id && String(current ?? '') !== String(newValue ?? '')) {
          return { id, value: newValue };
        }
        return null;
      })
      .filter(Boolean) as { id: number; value: string | number }[];

    if (changedFields.length === 0) {
      setCurrentModal(null);
      return;
    }

    console.log(changedFields);
    try {
      const response = await api.patch(
        `/devices/${equipment.id}/set-parameters`,
        changedFields
      );

      if (response.status === 200) {
        refetch();
        enqueueSnackbar(t('config.models.editSuccess'), {
          variant: 'success',
        });
        setCurrentModal(null);
      }
    } catch {
      enqueueSnackbar(t('config.models.editFailed'), { variant: 'error' });
    }
  };

  const handleEditWan = (vals: Record<string, string | number>) =>
    handleEdit('wans', currentWan, currentWanFieldList, vals);
  const handleEditLan = (vals: Record<string, string | number>) =>
    handleEdit('lans', currentLan, lanFields, vals);

  return (
    <RightContent>
      {/** WAN Configuration Card - Displays Wide Area Network settings */}
      <Card padding="24px">
        <MainHeader>
          <div className="divHeader">
            <Typography variant="h3">WAN {currentWan + 1}</Typography>
          </div>
          {/** Toggle buttons for switching between multiple WAN interfaces */}
          <div className="divHeader">
            <DescriptionPopper
              description={
                <DescriptionComponent>
                  {t('equipment.selectWan')}
                </DescriptionComponent>
              }
              placement="top"
              children={
                <EditButton onClick={() => setCurrentModal('selectWan')}>
                  <WifiPen size={18} />
                </EditButton>
              }
            />
            <DescriptionPopper
              description={
                <DescriptionComponent>
                  {t('equipment.editInfoWan')}
                </DescriptionComponent>
              }
              placement="top"
              children={
                <EditButton onClick={() => setCurrentModal('editWan')}>
                  <Pencil size={18} />
                </EditButton>
              }
            />
          </div>
        </MainHeader>

        {/** WAN Information Body*/}
        <CardBody modeView={modeView === 'grid'}>
          {wanFields.map((field) => {
            const wan = equipment?.wans?.[currentWan];
            const current =
              wan?.[field.field as keyof NetworkInterfaceType]?.value;

            // returns if wans doesn't contains previous configured fields
            if (wan?.[field.field as keyof NetworkInterfaceType] === null)
              return null;

            // Returns field's name and value in current selected WAN interface
            return (
              <InfoBadge key={field.field} modeView={modeView}>
                <BadgeTitle>{t(`equipment.wan.${field.field}`)}</BadgeTitle>
                {!loading ? (
                  <BadgeValue empty={current === ''}>
                    {current === '' ? t('equipment.empty') : current}
                  </BadgeValue>
                ) : (
                  <Skeleton
                    variant="rounded"
                    width={modeView === 'grid' ? '225px' : '100%'}
                    height={14}
                  />
                )}
              </InfoBadge>
            );
          })}
        </CardBody>
      </Card>

      {/** General Info */}
      <Card sx={{ maxWidth: '100%', height: '100%' }}>
        <div className="divHeader">
          <Typography variant="h3">
            {t('equipment.stats.generalInfo')}
          </Typography>
        </div>
        <ChartsGrid>
          {/* Column 1: Memory */}
          <ChartColumn>
            <PieArcChart
              data={memory}
              percentage={memoryUsagePercent}
              tooltipFormatter={memoryTooltipFormatter}
            />
            <Typography align="center" variant="h6">
              {t('equipment.stats.usedMemory')}
            </Typography>
          </ChartColumn>
          {/* Column 2: CPU */}
          <ChartColumn>
            <PieArcChart
              data={cpuUsage}
              percentage={Number(equipment.general?.cpuUsage?.value ?? 0)}
              tooltipFormatter={cpuTooltipFormatter}
            />
            <Typography align="center" variant="h6">
              {t('equipment.stats.usedCPU')}
            </Typography>
          </ChartColumn>
          {/* Coluna 3: Uptime */}
        </ChartsGrid>
        <UptimeInfoBadge>
          <UptimeBadgeTitle>{t('equipment.stats.uptime')}</UptimeBadgeTitle>
          <Typography variant="caption" color={theme.palette.white['06']}>
            {formatDurationShort(Number(equipment.general?.upTime?.value) || 0)}
          </Typography>
        </UptimeInfoBadge>
      </Card>

      {/** WAN Stats*/}
      <Card sx={{ maxWidth: '100%', height: '100%' }}>
        <WanStatsTitle variant="h3" gutterBottom>
          {t('equipment.stats.wan') + ' ' + (currentWan + 1)}
        </WanStatsTitle>
        <StatsData
          fieldValues={equipment?.wans?.[currentWan]}
          translatePath={'equipment.wan'}
        />
      </Card>

      {/** Lan and connection views */}
      <Card>
        <MainHeader>
          <div className="divHeader">
            <Typography variant="h3">
              {t('equipment.connections.title')}
            </Typography>
          </div>
          <div className="divHeader">
            {selectedPort && (
              <EditButton
                onClick={() => setSelectedPort(null)}
                sx={{ width: '32px' }}
              >
                <EthernetPort size={18} />
              </EditButton>
            )}
          </div>
        </MainHeader>

        <LanGrid>
          {/* Details Section */}
          <ConnectionDetailsStack>
            <Connection
              selectedPort={selectedPort}
              setSelectedPort={setSelectedPort}
              equipment={equipment}
            />
          </ConnectionDetailsStack>
        </LanGrid>
      </Card>

      {/** LAN Configuration Card - Displays Local Area Network settings */}
      <Card>
        <MainHeader>
          <div className="divHeader">
            <Typography variant="h3">LAN {currentLan + 1}</Typography>
          </div>

          <div className="divHeader">
            <DescriptionPopper
              description={
                <DescriptionComponent>
                  {t('equipment.selectLan')}
                </DescriptionComponent>
              }
              placement="top"
              children={
                <EditButton onClick={() => setCurrentModal('selectLan')}>
                  <WifiPen size={18} />
                </EditButton>
              }
            />
            <DescriptionPopper
              description={
                <DescriptionComponent>
                  {t('equipment.selectLan')}
                </DescriptionComponent>
              }
              placement="top"
              children={
                <EditButton onClick={() => setCurrentModal('editLan')}>
                  <Pencil size={18} />
                </EditButton>
              }
            />
          </div>
        </MainHeader>

        {/** LAN Information Body */}
        <CardBody modeView={modeView === 'grid'}>
          {lanFields.map((field) => {
            const lan = equipment?.lans?.[currentLan]; // storage equipment LAN field's values in the current selected LAN interface
            const current =
              lan?.[field.field as keyof NetworkInterfaceType]?.value;

            // returns if wans doesn't contains previous configured fields
            if (lan?.[field.field as keyof NetworkInterfaceType] === null)
              return null;

            return (
              // Returns field's name and value in current selected LAN interface
              <InfoBadge key={field.field} modeView={modeView}>
                <BadgeTitle>{t(`equipment.lan.${field.field}`)}</BadgeTitle>
                {!loading ? (
                  <BadgeValue empty={current === ''}>
                    {current === '' ? t('equipment.empty') : current}
                  </BadgeValue>
                ) : (
                  <Skeleton
                    variant="rounded"
                    width={modeView === 'grid' ? '225px' : '100%'}
                    height={14}
                  />
                )}
              </InfoBadge>
            );
          })}
        </CardBody>
      </Card>

      {/** WAN - PPPOE */}
      <ModalEditor
        open={currentModal === 'editWan'}
        setOpen={setCurrentModal}
        title={t('equipment.editInfoWan')}
        fieldList={currentWanFieldList}
        handleSubmit={handleEditWan}
        fieldValues={equipment?.wans?.[currentWan] ?? {}}
        icon="globe"
        translatePath="equipment.wan"
        validationSchema={wanSettingSchema}
        externalOptions={wanExternalOptions}
      />

      {/** Displays modal component to allow LAN fields edit */}
      <ModalEditor
        open={currentModal === 'editLan'}
        setOpen={setCurrentModal}
        title={t('equipment.editInfoLan')}
        fieldList={lanFields}
        handleSubmit={handleEditLan}
        fieldValues={equipment?.lans?.[currentLan] ?? {}}
        translatePath="equipment.lan"
        validationSchema={lanSettingSchema}
        externalOptions={lanExternalOptions}
      />

      {/** Select to change WAN */}
      <SelectAp
        title={t('equipment.wan.changeWan')}
        label={t('equipment.selectWan')}
        open={currentModal === 'selectWan'}
        setOpen={setCurrentModal}
        currentValue={currentWan}
        setCurrentValue={setCurrentWan}
        groupedOptions={wanGroupedOptions}
      />

      {/** Select to change LAN */}
      <SelectAp
        title={t('equipment.lan.changeLan')}
        label={t('equipment.selectLan')}
        open={currentModal === 'selectLan'}
        setOpen={setCurrentModal}
        currentValue={currentLan}
        setCurrentValue={setCurrentLan}
        currentOptions={
          (equipment?.lans ?? []).map((current, index) => ({
            index,
            name: String(current?.alias?.value ?? `LAN ${index + 1}`),
          })) || []
        }
      />
    </RightContent>
  );
}

export default General;
